import { type CardAISessionSummary } from '@shared/all/domains/electron';
import { type Factiii } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { type RouterOutputs } from '@shared/all/domains/trpc';

import { type BoardAgentAPI } from './remoteBoardAgent';

// Fixtures for the development ride (web + mobile), rendered through a seeded
// QueryClient. They cover only the DTO subset the board UI reads; cast the rest.

export const DEMO_SLUG = 'demo-board';
export const DEMO_SPACE_ID = 999999901;

const now = new Date();

const demoUser = {
  id: 999999901,
  name: 'Alex Demo',
  username: 'alexdemo',
  avatarKey: null,
  robohash: 'demo-alex',
  tag: 'HUMAN',
  types: []
};

export const DEMO_SPACE = {
  id: DEMO_SPACE_ID,
  name: 'Demo Board',
  spaceSlug: DEMO_SLUG,
  description: 'A demo board for the development tour',
  types: [],
  createdAt: now,
  updatedAt: now,
  pinnedPostId: null,
  bannerKey: null,
  avatarKey: null,
  robohash: 'demo-board',
  owners: [demoUser],
  rules: [],
  membersCount: 1,
  banned: false,
  banned_count: 0,
  factiiiIds: [],
  pinnedFactiiis: [],
  headerBoards: [],
  membership: {
    roles: ['OWNER', 'MODERATOR', 'MEMBER'],
    createdAt: now,
    expires: null
  },
  config: {
    defaultLayout: 'Board',
    spaceRenderType: 'BOTH',
    messageVisibility: 'ALL_MESSAGES',
    moderationScope: 'ALL_FACTIII'
  }
} as unknown as RouterOutputs['spaces']['get'];

const demoFactiii = (id: number, name: string, slug: string, pos: number) =>
  ({
    id,
    name,
    username: demoUser.username,
    description: null,
    slug,
    spaceSlug: DEMO_SLUG,
    status: 'APPROVED',
    groupId: null,
    avatarKey: null,
    bannerKey: null,
    replyConfig: undefined,
    // LIST is what makes a factiii render as a board column
    requirements: ['LIST'],
    rules: [],
    owner: demoUser,
    factiiiSlug: slug,
    data: null,
    createdAt: now,
    pos
  }) as unknown as Factiii;

export const DEMO_FACTIIIS = [
  demoFactiii(999999911, 'Backlog', 'backlog', 65535),
  demoFactiii(999999912, 'In Progress', 'in-progress', 131070),
  demoFactiii(999999913, 'Done', 'done', 196605)
];

const demoPost = (id: number, content: string, pos: number) =>
  ({
    id,
    slug: `demo-post-${id}`,
    title: null,
    content,
    voteCount: 1,
    voted: null,
    viewCount: 1,
    replies_count: 0,
    replies: [],
    createdAt: now,
    edited: false,
    anonymous: false,
    isPrivate: false,
    repost: null,
    topAnswer: null,
    pos,
    pinned: false,
    saved: false,
    user: demoUser,
    // The post DTO carries the space as its slug string
    space: DEMO_SLUG,
    uploads: [],
    taggedUsers: [],
    postFactiiisCount: [],
    awards: []
  }) as unknown as Post;

// Cards keyed by their column's factiii id
export const DEMO_POSTS: Record<number, Post[]> = {
  [DEMO_FACTIIIS[0].id]: [
    demoPost(999999921, 'Add a dark mode toggle to settings', 65535),
    demoPost(999999922, 'Fix sign-in button alignment on mobile', 131070)
  ],
  [DEMO_FACTIIIS[1].id]: [
    demoPost(999999923, 'Write the project README', 65535)
  ],
  [DEMO_FACTIIIS[2].id]: [demoPost(999999924, 'Set up this board', 65535)]
};

// A connected runner so the ride shows the AI surfaces (runner button, card
// AI icons, live runs) instead of empty set-up states. Structurally matches
// both platforms' OnlineRunner types.
export const DEMO_RUNNER = {
  socketId: 'demo-runner',
  runnerId: 999999931,
  name: 'MacBook Pro',
  platform: 'darwin'
};

// The In Progress card has a finished AI run awaiting review
const DEMO_SESSIONS: Record<string, CardAISessionSummary> = {
  '999999923': {
    taskId: 'demo-task',
    postId: '999999923',
    phase: 'review',
    mode: 'claude',
    provider: 'claude',
    // The tour has no runner behind it, so there's no thread to resume.
    agentSessionId: '',
    step: 3,
    diff: '',
    changedFiles: ['README.md'],
    commitMessage: 'Add project README',
    previewUrl: '',
    previewRunning: false,
    reproSteps: '',
    pendingQuestion: '',
    error: '',
    startedAt: Date.now() - 5 * 60_000,
    targetBranch: 'main',
    taskTitle: 'Write the project README',
    todos: [
      { content: 'Outline sections', status: 'completed' },
      { content: 'Document setup steps', status: 'completed' },
      { content: 'Write usage examples', status: 'completed' }
    ]
  }
};

// Only the methods the board tree + tour-opened modals call on mount. Deploy
// reports no skill yet, so its modal opens on the first-run creator screen.
export const DEMO_AGENT = {
  cardSessions: () => DEMO_SESSIONS,
  onSessionUpdate: () => () => {},
  onBoardUpdate: () => () => {},
  onStatus: () => () => {},
  onBoardLog: () => () => {},
  boardSession: () => ({ messages: [] }),
  boardSessions: () => ({ conversations: [] }),
  cardRemoteBranches: () => ['main'],
  bareWorkNames: () => [],
  getConfig: () => ({
    repoUrl: '',
    mainBranch: 'main',
    gitName: 'Demo',
    gitEmail: '',
    provider: 'claude',
    claudeLoggedIn: true,
    codexLoggedIn: false,
    hasGithubToken: true,
    githubTokenHint: '',
    onedrive: { connected: false }
  }),
  deployRunState: () => ({
    phase: 'idle',
    todos: [],
    logs: [],
    question: '',
    pendingCommand: '',
    error: '',
    summary: '',
    startedAt: 0,
    finishedAt: 0
  }),
  deployCreatorState: () => ({
    phase: 'idle',
    logs: [],
    draft: null,
    error: ''
  }),
  deployBranches: () => [],
  deployStatus: () => ({
    hasSkill: false,
    hasVariables: false,
    hasChecker: false,
    branch: 'main',
    pendingBranch: '',
    variables: []
  }),
  deployEnvState: () => ({ phase: 'idle', logs: [], result: null }),
  onDeployCreator: () => () => {},
  onDeployRun: () => () => {},
  onDeployEnv: () => () => {},
  broadcast: () => {},
  // Board AI Settings (ConfigView) subscribes to these on mount; no-op unsubs
  // keep the tour's settings step from touching the host.
  onClaudeAuth: () => () => {},
  onCodexAuth: () => () => {},
  onOneDriveAuth: () => () => {},
  deploySecretsStatus: () => ({ initialized: false })
} as unknown as BoardAgentAPI;
